import { useEffect, useRef, useCallback } from 'react'

export interface UseInfiniteScrollOptions {
  onLoadMore: () => void
  hasMore: boolean
  loading: boolean
  threshold?: number
  rootMargin?: string
  /**
   * El elemento que hace scroll. La lista vive en un contenedor con
   * `overflow` propio: con `root: null` el navegador recorta la interseccion
   * por ese contenedor y el `rootMargin` de anticipacion no vale de nada.
   */
  root?: Element | null
}

/**
 * El centinela sigue a la vista despues de cargar: hay que pedir otra pagina.
 *
 * `IntersectionObserver` solo avisa al ENTRAR en el area visible. Si una pagina
 * llega corta -o vacia- y el centinela no llega a salir, no vuelve a avisar y
 * el scroll se queda muerto aunque queden paginas.
 */
export function centinelaSigueVisible(centinela: DOMRect, area: DOMRect, margen = 0): boolean {
  return centinela.top <= area.bottom + margen && centinela.bottom >= area.top - margen
}

function pixeles(rootMargin: string): number {
  const primero = parseInt(rootMargin, 10)
  return Number.isFinite(primero) ? primero : 0
}

export function useInfiniteScroll({
  onLoadMore,
  hasMore,
  loading,
  threshold = 0,
  rootMargin = '200px',
  root = null,
}: UseInfiniteScrollOptions) {
  const loadingRef = useRef<HTMLDivElement | null>(null)

  // Keep latest values in refs so the observer below doesn't need to be torn
  // down and recreated on every render (recreating it while the sentinel is
  // still in view makes the browser fire the callback immediately, causing
  // a load-more loop right when the user reaches the bottom of the list).
  const onLoadMoreRef = useRef(onLoadMore)
  onLoadMoreRef.current = onLoadMore
  const hasMoreRef = useRef(hasMore)
  hasMoreRef.current = hasMore
  const loadingStateRef = useRef(loading)
  loadingStateRef.current = loading

  const handleIntersection = useCallback((entries: IntersectionObserverEntry[]) => {
    const target = entries[0]
    if (target.isIntersecting && hasMoreRef.current && !loadingStateRef.current) {
      onLoadMoreRef.current()
    }
  }, [])

  useEffect(() => {
    const observer = new IntersectionObserver(handleIntersection, {
      threshold,
      rootMargin,
      root,
    })

    const currentRef = loadingRef.current
    if (currentRef) {
      observer.observe(currentRef)
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef)
      }
    }
  }, [handleIntersection, threshold, rootMargin, root])

  // Al terminar una carga, si el centinela sigue a la vista se pide la siguiente.
  useEffect(() => {
    if (loading || !hasMore) return
    const centinela = loadingRef.current
    if (!centinela) return
    const area = root ? root.getBoundingClientRect() : new DOMRect(0, 0, window.innerWidth, window.innerHeight)
    if (centinelaSigueVisible(centinela.getBoundingClientRect(), area, pixeles(rootMargin))) {
      onLoadMoreRef.current()
    }
  }, [loading, hasMore, root, rootMargin])

  return { loadingRef }
}
